import { EventEmitter, Service } from '@timing71/common';
import EJSON from 'ejson';
import simpleddp from 'simpleddp';
import throttle from 'lodash.throttle';

import { AlkamelSocket } from "./socket.js";
import { getManifest, translate } from './translate.js';
import { oid } from './types.js';
import { dispatchAdded, dispatchChanged, dispatchRemoved } from './ddp.js';

try {
  EJSON.addType('oid', a => new oid(a));
}
catch {
  // maybe already added (HMR can cause this)
}

const randomString = (length) => ([...Array(length)].map(() =>(~~(Math.random()*36)).toString(36)).join(''));
const randomNum = (length) => ([...Array(length)].map(() =>(~~(Math.random()*10)).toString(10)).join(''));

export class AlKamel extends Service {
  constructor(...args) {
    super(...args);

    const source = this.service.source;
    const match = source.match(AlKamel.regex);
    this.feed = match[2];
    this.host = source.slice(source.indexOf('://') + 3, source.indexOf('.com') + 4);
    this.server = null;

    this.handleDataChange = throttle(
      this.handleDataChange.bind(this),
      1000
    );
    this.getManifest = this.getManifest.bind(this);
    this.translate = this.translate.bind(this);
    this.updateSession = this.updateSession.bind(this);

    this.sessionMonitor = null;

    this.isFormulaE = this.host === 'livetiming-formula-e.alkamelsystems.com';
    this._raceControlLastIndex = -1;
  }

  start(connectionService) {

    const uuid = this.service.uuid;

    class SpecificAKSSocket extends AlkamelSocket {
      constructor(url) {
        super(connectionService, url, uuid);
      }
    };

    this.server = new simpleddp({
      endpoint: `wss://${this.host}/sockjs/${randomNum(3)}/${randomString(8)}/websocket`,
      SocketConstructor: SpecificAKSSocket,
      autoConnect: false,  // We call connect() later
      clearDataOnReconnection: false
    });

    // Monkey-patch to avoid overlapping `id` properties in AKS data
    this.server.dispatchAdded = dispatchAdded;
    this.server.dispatchChanged = dispatchChanged;
    this.server.dispatchRemoved = dispatchRemoved;

    if (process.env.NODE_ENV === 'development' && typeof(window) !== 'undefined') {
      window._ddp = this.server;
    }

    this.server.on('disconnected', () => {
      this.emitWarning(null, 'Disconnected from upstream timing source, reconnecting...');
      this.server.connect();
    })

    this.server.connect().then(
      () => {
        this.emitInfo(null, 'Connected to upstream timing source');
        this.server.subscribe('livetimingFeed', this.feed).ready().then(
          () => {
            const feed = this.server.collection('feeds').fetch()[0];
            if (feed) {
              this.server.collection('session_info').onChange(this.updateSession);
              this.server.subscribe('sessions', feed.sessions || []);
              this.server.subscribe('sessionInfo', feed.sessions || []);
            }
          }
        )

      }
    );
  }

  updateSession() {
    const allInfo = this.server.collection('session_info').fetch();
    const currentSessionInfo = allInfo.find(s => !s.info?.closed) || allInfo[allInfo.length - 1];

    if (currentSessionInfo && currentSessionInfo.session?.value !== this.sessionMonitor?.sessionID?.value) {
      this.emitDebug(null, `Session change to ${currentSessionInfo.session.value}`);
      if (this.sessionMonitor?.sessionID?.value) { // Not the very first session we've seen
        this.onSessionChange();
      }

      if (this.sessionMonitor) {
        this.sessionMonitor.stop();
      }
      this.sessionMonitor = new SessionMonitor(
        currentSessionInfo.session,
        this.server
      );
      this.sessionMonitor.on('dataChange', this.handleDataChange);
      this.sessionMonitor.start();
    }
  }

  stop() {
    this.sessionMonitor && this.sessionMonitor.stop();
    this.server && this.server.disconnect();
  }

  getManifest(collections) {
    return getManifest(collections, this.isFormulaE);
  }

  translate(collections) {
    const nextState = translate(collections, this._raceControlLastIndex, this.isFormulaE);
    this._raceControlLastIndex = nextState.meta.raceControlLastIndex;
    return nextState;
  }

  handleDataChange(collections) {
    this.onManifestChange(this.getManifest(collections));

    const nextState = this.translate(collections);
    this._raceControlLastIndex = nextState.meta.raceControlLastIndex;
    this.onStateChange(nextState);
  }
}

// These are (usually) camel-cased...
const SESSION_SUBSCRIPTIONS = [
  'entry',
  'trackInfo',
  'standings',
  'sessionStatus',
  'weather',
  'bestResults',
  'raceControl',
  'sessionBestResultsByClass',
  'sessionClasses',
];

const FORMULA_E_SUBSCRIPTIONS = [
  'sessionAttackMode',
]

// ...but these are (usually) snake-cased.
const SESSION_COLLECTIONS = [
  'track_info',
  'standings',
  'session_info',
  'session_status',
  'weather',
  'best_results',
  'race_control',
  'sessionBestResultsByClass', // ...except when they're not.
  'session_entry', // And this one is named differently
  'session_classes',
];

const FORMULA_E_COLLECTIONS = [
  'attackMode', // so is this
];

class SessionMonitor extends EventEmitter {
  constructor(sessionID, ddp) {
    super();
    this.sessionID = sessionID;
    this.ddp = ddp;

    this.start = this.start.bind(this);
    this.stop = this.stop.bind(this);
    this._currentSessionCollections = this._currentSessionCollections.bind(this);
    this.getSessionCollection = this.getSessionCollection.bind(this);
    this._onDataChange = this._onDataChange.bind(this);

    this._subs = [];
    this._obs = [];
    this._pending = 0;
  }

  start() {
    const initCollection = (collection) => {
      const observer =this.ddp.collection(collection).onChange(this._onDataChange);
      this._obs.push(observer);
    }

    const initSubscription = (collection) => {
      this._pending++;
      const sub = this.ddp.subscribe(collection, this.sessionID);
      this._subs.push(sub);
      sub.ready().finally(() => {
        this._pending--;
        if (this._pending === 0) {
          this._onDataChange();
        }
      });
    }

    SESSION_COLLECTIONS.forEach(initCollection);
    if (this.isFormulaE) {
      FORMULA_E_COLLECTIONS.forEach(initCollection);
    }

    SESSION_SUBSCRIPTIONS.forEach(initSubscription);
    if (this.isFormulaE) {
      FORMULA_E_SUBSCRIPTIONS.forEach(initSubscription);
    }
  }

  stop() {
    while(this._subs.length > 0) {
      this._subs.pop().stop();
    }
    while(this._obs.length > 0) {
      this._obs.pop().stop();
    }
  }

  _onDataChange() {
    Promise.resolve().then(
      () => {
        if (this._pending === 0) {
          const collections = this._currentSessionCollections();
          this.emit('dataChange', collections);
        }
      }
    );
  }

  getSessionCollection(collection) {
    return mapToRelevantKey(
      this.ddp.collection(collection).filter(c => c.session?.value === this.sessionID?.value).fetch()[0] || {}
    );
  }

  _currentSessionCollections() {
    const collections = Object.fromEntries(
      SESSION_COLLECTIONS.map(
        collection => {
          return ([
            collection,
            this.getSessionCollection(collection)
          ]);
        }
      )
    );

    collections['session'] = this.ddp.collections.sessions.find(s => s._id === this.sessionID.value);

    return collections;
  }
}

const mapToRelevantKey = (obj) => {
  const relevantKey = Object.keys(obj).filter(k => k !== 'id' && k !== '_id' && k !== 'session')[0];
  if (relevantKey) {
    return obj[relevantKey];
  }
  return {};
};

AlKamel.regex = /livetiming(-formula-e)?\.alkamelsystems\.com\/([0-9a-zA-Z_]+)/;
